import { useState, ChangeEvent, useEffect } from "react";
import { Dropdown, Button, FormControl, FormCheck } from "react-bootstrap";

import CustomDropdownToggle from "../common/CustomDropdownToggle";
import CustomDropdownMenu from "../common/CustomDropdownMenu";
import CustomFormCheck from "../common/CustomFormCheck";
import { ReportFilterItem } from "types/Reports";

type ReportDropdownProps = {
    title: string;
    items: ReportFilterItem[];
    selectedItems: ReportFilterItem[];
    searchTextPlaceholder: string;
    limit: number;
    limitText: string;
    emptyText: string;
    onChange: (types: ReportFilterItem[]) => void;
};

export default function ReportDropdown({
    title,
    items,
    selectedItems,
    searchTextPlaceholder,
    limit,
    limitText,
    emptyText,
    onChange,
}: Readonly<ReportDropdownProps>) {
    const [searchText, setSearchText] = useState("");
    const [selectedList, setSelectedList] = useState<ReportFilterItem[]>([]);
    const [filteredList, setFilteredList] = useState<ReportFilterItem[]>([]);
    const [show, setShow] = useState(false);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setFilteredList(
            items.filter(({ name }) =>
                name.toLowerCase().includes(e.target.value.toLowerCase())
            )
        );
    };

    const handleItemOnChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked, value } = e.target;
        const retrievedItem = filteredList.find((i) => i.id === value);
        let newList = [...selectedList];
        const isExisted = newList.find((i) => i.id === value);

        if (checked && retrievedItem && !isExisted) {
            if (newList.length > limit) {
                return;
            }

            newList.push(retrievedItem);
        } else if (!checked && retrievedItem && isExisted) {
            newList = newList.filter((i) => i.id !== value);
        }

        setSelectedList(newList);
    };

    const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;

        if (!checked) {
            setSelectedList([]);
            return;
        }

        if (items.length <= limit) {
            setSelectedList(items);
            return;
        }

        setSelectedList(items.slice(0, limit));
    };

    const clearFilter = () => {
        setSelectedList([]);
    };

    const applyFilter = () => {
        onChange([...selectedList]);
        setShow(false);
    };

    const handleOpenMenu = (isOpen: boolean) => {
        setShow(isOpen);
        setSelectedList(selectedItems);
        setSearchText("");
        setFilteredList(items);
    };

    useEffect(() => {
        setFilteredList(items);
    }, [items]);

    useEffect(() => {
        setSelectedList(selectedItems);
    }, [selectedItems]);

    const shownItemContainerClasses = filteredList.length === 0 ?
        'check-list report-dropdown-menu-form-check-list report-dropdown-menu-form-check-list--empty' :
        'check-list report-dropdown-menu-form-check-list';

    return (
        <Dropdown show={show} onToggle={handleOpenMenu} className='report-dropdown order-1 order-sm-0'>
            <CustomDropdownToggle className='report-dropdown-toggle'>
                <div className="report-dropdown-toggle-title">
                    {`${title}:`}
                </div>
                <div className="report-dropdown-toggle-count">
                    <span>{`${selectedItems.length} selected`}</span>
                    <i className="material-icons down ml-2">
                        keyboard_arrow_down
                    </i>
                </div>
            </CustomDropdownToggle>

            <CustomDropdownMenu className="report-dropdown-menu-container">
                <h6 className="report-dropdown-menu-title">Select {title}</h6>
                <div className="d-flex justify-content-between report-dropdown-menu-select-container">
                    <CustomFormCheck
                        key={`select-all-${title}`}
                        className="d-flex justify-content-between align-items-center page-select-all report-dropdown-menu-form-check-all"
                        name={`select-all-${title}`}
                        value={`select-all-${title}`}
                        checked={selectedList.length === items.length || selectedList.length === limit}
                        custom
                        onChange={handleSelectAll}
                        id={`select-all-${title}`}
                        label={<span className="ml-2 page-select-all-label">Select All</span>}
                    />
                    <FormControl
                        className="page-search report-dropdown-menu-form-search"
                        placeholder={searchTextPlaceholder}
                        aria-label={`search-${title}`}
                        onChange={handleSearch}
                        value={searchText}
                        data-testid="page-search"
                    />
                </div>

                <div className={shownItemContainerClasses}>
                    {
                        filteredList.length === 0 &&
                        <div className="report-dropdown-menu-form-check-list-empty-message">{emptyText}</div>
                    }
                    {
                        filteredList
                        .map((item: ReportFilterItem) => {
                            const isChecked = selectedList.some((i) => i.id === item.id);

                            return (
                                <FormCheck
                                    key={item.id}
                                    className="report-dropdown-menu-form-check"
                                    name={`selected-${item.name}`}
                                    value={item.id}
                                    checked={isChecked}
                                    disabled={!isChecked && selectedList.length === limit}
                                    custom
                                    onChange={handleItemOnChange}
                                    id={item.id}
                                    label={
                                        <div className="report-dropdown-menu-fc-label-container">
                                            <span className="report-dropdown-menu-fc-label-name" title={item.name}>{item.name}</span>
                                            <span className="report-dropdown-menu-fc-label-desc">Network ID: {item.networkId}</span>
                                        </div>
                                    }
                                />
                            );
                        })
                    }
                </div>
                <p className="report-dropdown-limit">
                    {selectedList.length}/{items.length} selected (Maximum of {limit} {limitText} can be selected for a report)
                </p>
                <div className="d-flex justify-content-between mt-3">
                    <Button
                        variant="dark"
                        onClick={clearFilter}
                        size="sm"
                        className="px-md-3"
                    >
                        CLEAR
                    </Button>
                    <Button
                        className="px-md-3"
                        variant="primary"
                        onClick={applyFilter}
                        size="sm"
                    >
                        DONE
                    </Button>
                </div>
            </CustomDropdownMenu>
        </Dropdown>
    );
}
